<template>
  <div class="ma-4">
    <v-snackbar
      v-model="msg"
      top
      :color="snackbarColor"
      :timeout="3000"
      elevation="4"
      class="rounded-lg"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{
          snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
        }}</v-icon>
        {{ text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="msg = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-card class="rounded-lg">
      <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg">
        <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
          <v-icon>navigate_before</v-icon>
        </v-btn>

        <span class="text-h6 font-weight-medium grey--text text--darken-3"
          >Staff Reports</span
        >

        <v-spacer></v-spacer>

        <v-btn
          small
          outlined
          color="primary"
          class="rounded-lg mr-2"
          :disabled="isButtonsDisabled"
        >
          <v-icon left small>mdi-printer-outline</v-icon>
          print
        </v-btn>

        <v-btn
          small
          outlined
          color="primary"
          class="rounded-lg mr-2"
          :disabled="isButtonsDisabled"
          @click="handleFileDownload"
          :loading="isDownloading"
        >
          <v-icon left small>mdi-download</v-icon>
          download
        </v-btn>
      </v-app-bar>

      <v-divider></v-divider>

      <v-card-text class="pa-6 grey lighten-5">
        <v-row dense>
          <v-col cols="12" md="2">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              max-width="290"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="reportFilters.startDate"
                  label="Start Date"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilters.startDate"
                @input="startDateMenu = false"
                color="primary"
                class="rounded-lg"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              max-width="290"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="reportFilters.endDate"
                  label="End Date"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="reportFilters.endDate"
                @input="endDateMenu = false"
                color="primary"
                class="rounded-lg"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-autocomplete
              v-model="reportFilters.staffName"
              :items="staff"
              label="Staff"
              item-text="Name"
              item-value="Name"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="reportFilters.reportType"
              :items="reportTypes"
              label="Report Type"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
              @change="onReportTypeChange"
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              block
              height="40"
              class="rounded-lg"
              :loading="isLoading"
              :disabled="!isFormValid"
              elevation="1"
              @click="getQueryData(false)"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="pa-6">
        <v-data-table
          :headers="tableHeaders"
          :items="reportData"
          :loading="isLoading"
          loading-text="Loading Reports..."
          :items-per-page="10"
          :search="search"
          :footer-props="{
            'items-per-page-options': [5, 10, 20, 50],
            showFirstLastPage: true,
            'items-per-page-text': 'Rows per page:',
            class: 'rounded-lg',
          }"
          class="elevation-1 rounded-lg"
        >
          <template v-slot:top>
            <v-toolbar
              flat
              dense
              class="grey lighten-4 rounded-t-lg px-3"
              height="80"
            >
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search records"
                single-line
                hide-details
                dense
                outlined
                class="rounded-lg"
                background-color="white"
              ></v-text-field>
            </v-toolbar>
          </template>

          <template v-slot:no-data>
            <div class="pa-8 text-center">
              <v-icon size="64" color="grey lighten-2"
                >mdi-clipboard-text-outline</v-icon
              >
              <div class="text-subtitle-1 grey--text text--darken-1 mt-4">
                No reports available
              </div>
              <div class="text-body-2 grey--text mt-1">
                Try adjusting your search criteria
              </div>
            </div>
          </template>

          <template v-slot:item.select="{ item }">
            <v-btn
              small
              text
              color="primary"
              class="px-2"
              @click="viewReport(item)"
            >
              <v-icon left small>mdi-eye</v-icon>
              View
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
const url = require("../../client_config/config").url;
import { setUserTz } from "../../../utils/timezone";
import { getTableHeaders } from "../../constants/TableHeaders";
import { REPORT_TYPES } from "../../constants/ReportTypes";
import { mapActions, mapGetters } from "vuex";
import ReportFilters from "@/mixins/reportFilters.js";

export default {
  mixins: [ReportFilters],
  data: () => ({
    msg: false,
    text: "",
    snackbarColor: "success",
    isLoading: false,
    clientID: "",
    staff: [],
    reportTypes: REPORT_TYPES,
    requiredField: [(v) => !!v || "This field is required"],
    reportData: [],
    startDateMenu: false,
    endDateMenu: false,
    reportHeader: "",
    search: "",
    userState: localStorage.getItem("userState"),
    formModified: true,
    isDownloading: false,
    reportFilters: {
      startDate: "",
      endDate: "",
      staffName: "",
      reportType: "",
    },
    reportFilterName: "ReportFilters_ShiftReports",
  }),

  computed: {
    ...mapGetters("timezones", ["getUserTimezone", "getDefaultTz"]),

    tableHeaders() {
      return getTableHeaders(this.reportFilters.reportType.toLowerCase());
    },

    userTimezone() {
      const data = {
        userTimezone: this.getUserTimezone,
        defaultTz: this.getDefaultTz,
      };
      return setUserTz(data);
    },

    isFormValid() {
      return this.reportFilters.staffName && this.reportFilters.reportType;
    },

    isButtonsDisabled() {
      return !this.reportData.length || this.formModified;
    },
  },

  created() {
    this.getStaff();
    this.reportFilters =
      this.getReportFilters(this.reportFilterName) ?? this.reportFilters;
  },

  methods: {
    ...mapActions({
      getAdminReports: "reports/getAdminReports",
    }),

    async getStaff() {
      try {
        if (!this.userRole) {
          this.userRole = "";
        }
        const response = await axios.get(
          `${url}/employees/staff/${this.userState}`
        );
        this.staff = response.data.employees;
      } catch (error) {
        console.error("Error fetching staff data:", error);
      }
    },

    async getQueryData(generateFile = false) {
      if (!this.isFormValid) {
        this.showMessage("Please fill in all required fields", "error");
        return;
      }
      try {
        this.isLoading = true;
        const params = this.getQueryParams(generateFile);
        await this.getAdminReports(params);
        if (!generateFile) {
          const response = await this.getAdminReports(params);

          this.reportData = response.data.results;
          this.formModified = false;
        }
      } catch (error) {
        this.showMessage(
          "Error fetching report data: " + error.message,
          "error"
        );
      } finally {
        this.isLoading = false;
      }
    },

    viewReport(item) {
      console.log(item);
    },

    onReportTypeChange(type) {
      this.reportHeader = type.toLowerCase();
      this.reportData = [];
    },

    getQueryParams(generateFile) {
      const baseQuery = {
        startDate: this.reportFilters.startDate,
        endDate: this.reportFilters.endDate,
        tz: this.getDefaultTz,
        staffName: this.reportFilters.staffName,
        state: this.userState,
        generateFile,
      };

      const paramMap = {
        mars: {
          endpoint: "reports/create/mars-report",
          reportType: this.reportFilters.reportType.toLowerCase(),
          query: baseQuery,
        },
        adls: {
          endpoint: "reports/create/adls-report",
          reportType: this.reportFilters.reportType.toLowerCase(),
          query: baseQuery,
        },
        behaviors: {
          endpoint: "reports/create/behaviors-report",
          reportType: this.reportFilters.reportType.toLowerCase(),
          query: baseQuery,
        },
        "ut incidents": {
          endpoint: "reports/create/ut-incidents-report",
          reportType: this.reportFilters.reportType.toLowerCase(),
          query: baseQuery,
        },
        "nv incidents": { test: "nv incident reports" },
      };

      const reportKey = this.reportFilters.reportType.toLowerCase();
      return (
        paramMap[reportKey] || {
          error: `Unsupported report type: ${this.reportFilters.reportType}`,
        }
      );
    },

    async handleFileDownload() {
      try {
        this.isDownloading = true;
        await this.getQueryData(true);
        this.showMessage("File downloaded successfully", "success");
      } catch (error) {
        this.showMessage("Error downloading file: " + error.message, "error");
      } finally {
        this.isDownloading = false;
      }
    },

    showMessage(message, color) {
      this.text = message;
      this.snackbarColor = color;
      this.msg = true;
    },
  },
  watch: {
    reportFilters: {
      handler(newVal) {
        this.setReportFilters(this.reportFilterName, newVal);
        this.formModified = true;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}
</style>
